import { useInfiniteQuery } from 'react-query'

import { useQueryApi, useMutationApi, api } from 'utils/api'

import type { UseQueryOptions } from 'react-query'
import type {
  AssessmentClasses,
  LearningGroupItem,
  Packet,
  MyAssessment,
  MyAssessmentDetail,
  MyStudentAssessmentResult,
  AssessmentClassAnalysis,
} from 'types/assessment'

export interface ResponseSuccessAssessmentClasses {
  // confirm need change from backend
  questionsPacket: any
  data: AssessmentClasses[]
  meta: any
}

export function useAPIGetAssessmentClass() {
  return useQueryApi('/teachers/v1alpha1/learninggroups')
}

export interface ResponseSuccessGenerateAssessment {
  learningGroups: LearningGroupItem[]
  meta: any
}

export interface GenerateAssessmentRequestBody {
  id: string
  noTimeLimit?: boolean
  maxSubmission?: string
  learningGroups: LearningGroupItem[]
}

export function useMutateAPIGenerateAssessment() {
  return useMutationApi<
    ResponseSuccessGenerateAssessment,
    Error,
    GenerateAssessmentRequestBody
  >('/teachers/v1alpha1/assessment/create', {
    axiosConfig: {
      method: 'POST',
    },
  })
}

export function useAPIGetPacketById(id) {
  return useQueryApi<Packet>(
    `/teachers/v1alpha1/assessment/questionspacket/${id}`,
    {
      axiosConfig: {
        validateStatus: (status) => status < 500,
      },
    }
  )
}

export interface ResponseSuccessSearchPacket {
  data: Packet[]
  meta: any
}

export interface SearchAssessmentParams {
  subject: string
  phase: string
  limit: string | number
}

export function useAPISearchAssessmentPacket(params: SearchAssessmentParams) {
  return useInfiniteQuery(
    ['search-packets', params],
    async ({ pageParam = 0 }): Promise<ResponseSuccessSearchPacket> => {
      try {
        const response = await api<ResponseSuccessSearchPacket>(
          '/teachers/v1alpha1/assessment/questionspackets',
          { params: { ...params, offset: pageParam } }
        )
        return response
      } catch (error) {
        // Not found result return 404
        if (error.response.status === 404) {
          return {
            data: [],
            meta: {
              limit: 0,
              offset: 0,
              total: 0,
            },
          }
        } else {
          throw error
        }
      }
    },
    {
      getNextPageParam(_, pages) {
        const { offset, limit, total } = pages[pages.length - 1].meta
        const nextOffset = offset + limit
        if (nextOffset < total) return nextOffset
      },
      enabled: !!params.subject && !!params.phase,
    }
  )
}

interface ResponseDownloadDistributePacket {
  questionPdfUrl: string
  solutionPdfUrl: string
}
export function useAPIDownloadDistributePacket(
  id: string,
  options?: UseQueryOptions<ResponseDownloadDistributePacket>
) {
  return useQueryApi<ResponseDownloadDistributePacket>(
    `/teachers/v1alpha1/assessment/questionspacket/${id}/pdf`,
    {
      queryConfig: options,
    }
  )
}

interface ResponseSuccessMyAssessment {
  data: MyAssessment[]
  meta: {
    total: number
    limit: number
    offset: number
  }
}

export function useMyAssesmentAPI(limit) {
  return useInfiniteQuery(
    ['/assessment', limit],
    async ({ pageParam = 0 }): Promise<ResponseSuccessMyAssessment> => {
      const response = await api<ResponseSuccessMyAssessment>(
        '/teachers/v1alpha1/assessment',
        {
          params: { limit, offset: pageParam },
        }
      )
      return response
    },
    {
      getNextPageParam(lastPage) {
        const { offset, limit, total } = lastPage.meta
        const nextOffset = offset + limit
        if (nextOffset < total) return nextOffset
      },
    }
  )
}

interface ResponseSuccessMyAssessmentDetailId {
  data: MyAssessmentDetail
  meta: {
    total: number
    limit: number
    offset: number
  }
}

export function useAPIGetMyAssessmentDetailById(
  id: string,
  options?: UseQueryOptions<ResponseSuccessMyAssessmentDetailId>
) {
  return useQueryApi<ResponseSuccessMyAssessmentDetailId>(
    `/teachers/v1alpha1/assessment/${id}/detail`,
    {
      queryConfig: options,
    }
  )
}

interface ResponseSuccessMyAssessmentScoring {
  message: string
}

interface RequestBodyMyAssessmentScoring {
  studentAnswerScore: Array<{ assessmentIntakeId: string; isCorrect: boolean }>
}

export function useMutateAPIMyAssessmentScoring() {
  return useMutationApi<
    ResponseSuccessMyAssessmentScoring,
    Error,
    RequestBodyMyAssessmentScoring
  >('/teachers/v1alpha1/assessment/answers', {
    axiosConfig: {
      method: 'PUT',
    },
  })
}

interface ResponseSuccessMyStudentAssessmentResult {
  data: MyStudentAssessmentResult
  meta: any
}

export function useAPIMyStudentAssessmentResult(
  assessmentId: string,
  studentId: string
) {
  return useQueryApi<ResponseSuccessMyStudentAssessmentResult>(
    `/teachers/v1alpha1/assessment/${assessmentId}/students/${studentId}/results`,
    {
      queryConfig: {
        enabled: !!assessmentId && !!studentId,
      },
    }
  )
}

export function useAPIAssessmentAnalysisClass(
  assessmentId: string,
  levelId: string,
  options?: UseQueryOptions<AssessmentClassAnalysis>
) {
  return useQueryApi<AssessmentClassAnalysis>(
    `/teachers/v1alpha1/assessment/${assessmentId}/detail/${levelId}`,
    {
      queryConfig: options,
    }
  )
}
